import { useCallback, useMemo } from 'react'
import { atom, getDefaultStore, useAtomValue, useSetAtom } from 'jotai'
import { pinElementToBottom, type PinToBottomOptions } from '@renderer/utils/pin-to-bottom'
import { useIsActiveTurn } from '../../../store'
import type { ScrollToProps } from '../../../types'
import { observeScrollIntent } from '../hooks/use-scroll-intent'

// ---------------------------------------------------------------------------
// Scroll state for the message list.
//
// Module-scoped atoms rather than per-list state: the floating scroll bar, the
// chat input, and the queued-messages panel all steer the same scroller from
// outside the list's subtree. `MessageList` resets them per conversation.
//
// Reads and writes are split into two hooks: the flag values flip on every
// scroll event, so action-only consumers (chat input, submit hooks) subscribe
// through `useMessageListScrollActions` and never re-render on them.
//
// Files inside `chat/components/chat/**` import this module directly; everything else
// goes through the `@renderer/chat` barrels (which re-export it) — the direct
// path exists because the barrel also exports the list component itself, and
// importing it from inside the tree would be circular.
// ---------------------------------------------------------------------------

export const autoScrollAtom = atom(true)
const scrollToEndAtom = atom(false)

/**
 * The scroller's DOM identity, owned here. Everything that steers the list
 * from outside its subtree resolves the element through this accessor —
 * never a hand-typed id string.
 */
export const MESSAGE_LIST_ELEMENT_ID = 'message-list'

export function getMessageListElement(): HTMLElement | null {
  return document.getElementById(MESSAGE_LIST_ELEMENT_ID)
}

// Only the mounted scroller can measure the list (it owns the rAF-coalesced
// scrollHeight read), but layout changes elsewhere — e.g. a submessage group
// collapsing — invalidate the derived flags. Those callers request a
// remeasure through this atom instead of reading the DOM themselves. A plain
// counter, not a boolean: every request must notify, and jotai atoms bail out
// of a write that doesn't change the value.
const remeasureRequestAtom = atom(0)

export function registerScrollFlagsRemeasure(handler: () => void): () => void {
  return getDefaultStore().sub(remeasureRequestAtom, handler)
}

export function requestScrollFlagsRemeasure() {
  getDefaultStore().set(remeasureRequestAtom, (n) => n + 1)
}

/**
 * THE settle pin for the message list: snap to the bottom edge and hold it
 * there while layout settles (rows laying out, markdown reflowing, the view's
 * bottom inset landing frames after the first scroll). A thin composition of
 * the canonical pieces — `pinElementToBottom` drives the window, and takeover
 * is read through the canonical scroll-intent classifiers (wheel, touch drag,
 * scroll keys), so scrolling up always hands control straight back.
 *
 * Callers add their own release conditions via `shouldContinue` (e.g. a
 * stream taking ownership of the scroller). Returns a release for
 * unmount/re-arm.
 */
export function pinMessageListWhileSettling(options: PinToBottomOptions = {}): () => void {
  const el = getMessageListElement()
  if (!el) return () => {}

  let userTookOver = false
  const releaseIntent = observeScrollIntent(el, (direction) => {
    if (direction === 'up') userTookOver = true
  })

  return pinElementToBottom(el, {
    ...options,
    shouldContinue: () => !userTookOver && (options.shouldContinue?.() ?? true),
    onRelease: () => {
      releaseIntent()
      options.onRelease?.()
    },
  })
}

/** The scroll flags — values flip per scroll event, so only subscribe where they render. */
export const useMessageListScrollFlags = () => {
  const autoScroll = useAtomValue(autoScrollAtom)
  const showScrollToEnd = useAtomValue(scrollToEndAtom)
  return { autoScroll, showScrollToEnd }
}

/**
 * Scroll writes only — no subscription to the flag atoms, so callers that
 * merely steer the scroller (chat input, submit hooks, floating-bar slots)
 * don't re-render per scroll event. The returned object's identity changes
 * only with the active turn (the scroll helpers key smooth-vs-instant off it).
 *
 * Edge jumps are instant, not smooth. Beyond feeling snappier on long
 * chats, a smooth top-jump from the very bottom races the follow effect:
 * the animation's first scroll events still measure "near bottom", which
 * re-engages auto-follow and snaps the view straight back down. An instant
 * jump completes before the first measure, so the flags settle correctly.
 */
export const useMessageListScrollActions = () => {
  const setAutoScroll = useSetAtom(autoScrollAtom)
  const setShowScrollToEnd = useSetAtom(scrollToEndAtom)
  const scrollTo = useScrollTo()
  const scrollToMessage = useScrollToMessage()

  return useMemo(() => {
    return {
      setAutoScroll,
      setShowScrollToEnd,
      scrollToStart: (options: Partial<ScrollToProps> = {}) => {
        scrollTo({ direction: 'top', behavior: 'instant', ...options })
        setShowScrollToEnd(true)
        setAutoScroll(false)
      },
      scrollToEnd: (options: Partial<ScrollToProps> = {}) => {
        scrollTo({ direction: 'bottom', behavior: 'instant', ...options })
        setShowScrollToEnd(false)
        setAutoScroll(true)
      },
      scrollToMessage,
    }
  }, [scrollTo, scrollToMessage, setAutoScroll, setShowScrollToEnd])
}

export const useScrollTo = () => {
  const isTurnActive = useIsActiveTurn()
  return useCallback(
    ({ direction = 'top', behavior = 'smooth' }: ScrollToProps) => {
      const el = getMessageListElement()
      if (!el) return
      el.scrollTo({
        top: direction === 'bottom' ? el.scrollHeight : 0,
        behavior: behavior || (isTurnActive ? 'instant' : 'smooth'),
      })
    },
    [isTurnActive],
  )
}

/**
 * Scroll a message to center. Returns whether the scroll happened — the row
 * must be in the DOM (every message mounts with the list now), so callers can
 * retry per frame until the message arrives.
 */
export const useScrollToMessage = () => {
  const isTurnActive = useIsActiveTurn()
  const setAutoScroll = useSetAtom(autoScrollAtom)
  return useCallback(
    (messageId: string | null): boolean => {
      if (!messageId) {
        return false
      }

      const messageEl = document.getElementById(`message-${messageId}`)
      if (!messageEl) {
        return false
      }

      // Targeting a specific message is an intentional departure from the
      // bottom edge, so disengage auto-follow up front (like scrollToStart
      // does) rather than waiting for handleScroll's measure. While engaged,
      // the bottom pin treats anything within 50px of the bottom as its own —
      // a smooth scroll's early frames sit in that window, so a layout change
      // mid-animation would re-pin to the bottom, cancelling the animation and
      // stranding the user on the latest message instead of the target.
      setAutoScroll(false)
      messageEl.scrollIntoView({
        behavior: isTurnActive ? 'instant' : 'smooth',
        block: 'center',
      })
      return true
    },
    [isTurnActive, setAutoScroll],
  )
}
